import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

function Review() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { form, projectName, fileName, isEditing, projectId } = state || {};

  const structureLabels = { screen_enclosure: 'Screen Enclosure', screen_porch: 'Screen Porch', mixed: 'Mixed Structure' };

  const handleCalculate = async () => {
    try {
      const payload = {
        structureType: form.structureType,
        dimensions: {
          totalWidth: form.totalWidth,
          totalLength: form.totalLength,
          height: form.height,
          roofSlope: form.roofSlope,
          rafterSpacing: form.rafterSpacing || null,
          longStrips: form.longStrips ? parseInt(form.longStrips) : null,
          shortStrips: form.shortStrips ? parseInt(form.shortStrips) : null,
          longLength: form.longLength || null,
          shortLength: form.shortLength || null
        },
        structuralProfiles: {
          mainBeamProfile: form.mainBeamProfile,
          rafterProfile: form.rafterProfile,
          columnType: form.columnType,
          numberOfColumns: form.numberOfColumns
        },
        columnCutType: form.columnCutType,
        columnInsertSize: form.columnInsertSize,
        numberOfInserts: form.numberOfInserts,
        doors: {
          numberOfDoors: form.numberOfDoors,
          doorSize: form.doorSize
        },
        screenType: form.screenType,
        kickPlate: form.kickPlate,
        fanBeam: form.fanBeam,
        egutterType: form.egutterType,
        spanTable: form.spanTable || null
      };

      const res = await axios.post('/api/calculate', payload);
      if (isEditing && projectId) {
        await axios.put(`/api/projects/${projectId}`, {
          projectName,
          structureType: form.structureType,
          fileName,
          results: { ...res.data.results, _form: form }
        });
      }
      navigate('/results', { state: { results: res.data.results, projectName, fileName, form, isEditing, projectId } });
    } catch (err) {
      alert('Error calculating materials.');
    }
  };

  if (!form) return <div>No data found. <a href="/">Go back</a></div>;

  return (
    <div>
      <div className="page-header">
        <div className="breadcrumb"><a href="/">Home</a> / New Project / <strong>Review</strong></div>
        <h1 className="page-title">Review Project Data</h1>
        <p className="page-subtitle">Confirm all information before calculating materials</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3>Project Summary</h3>
            <button className="btn-secondary" style={{ padding: '4px 12px', fontSize: '13px' }} onClick={() => navigate('/upload')}>✏️ Edit</button>
          </div>
          {[['Project Name', projectName], ['Structure Type', structureLabels[form.structureType]], ['Plan File', fileName], ['Date', new Date().toLocaleDateString()]].map(([label, value]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: '14px' }}>
              <span style={{ color: '#666' }}>{label}</span>
              <span style={{ fontWeight: '500' }}>{value}</span>
            </div>
          ))}
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3>Structural Profiles</h3>
            <button className="btn-secondary" style={{ padding: '4px 12px', fontSize: '13px' }} onClick={() => navigate('/form', { state })}>✏️ Edit</button>
          </div>
          {[
            ['Main Beam', form.mainBeamProfile],
            ['Rafter Profile', form.rafterProfile],
            ['Column Type', form.columnType],
            ['Number of Columns', form.numberOfColumns],
            ['Column Insert Size', form.columnInsertSize === 'auto' ? 'Auto (same as column)' : form.columnInsertSize === 'none' ? 'None' : `${form.columnInsertSize} inches`],
            ['Number of Inserts', form.numberOfInserts || `${form.numberOfColumns || '—'} (same as columns)`],
          ].map(([label, value]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: '14px' }}>
              <span style={{ color: '#666' }}>{label}</span>
              <span style={{ fontWeight: '500' }}>{value}</span>
            </div>
          ))}
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3>General Dimensions</h3>
            <button className="btn-secondary" style={{ padding: '4px 12px', fontSize: '13px' }} onClick={() => navigate('/form', { state })}>✏️ Edit</button>
          </div>
          {[['Total Width', form.totalWidth + ' ft'], ['Total Length', form.totalLength + ' ft'], ['Height', form.height + ' ft'], ['Roof Slope', form.roofSlope]].map(([label, value]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: '14px' }}>
              <span style={{ color: '#666' }}>{label}</span>
              <span style={{ fontWeight: '500' }}>{value}</span>
            </div>
          ))}
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3>Screen & Doors</h3>
            <button className="btn-secondary" style={{ padding: '4px 12px', fontSize: '13px' }} onClick={() => navigate('/form', { state })}>✏️ Edit</button>
          </div>
          {[
            ['Screen Type', form.screenType],
            ['Number of Doors', form.numberOfDoors],
            ['Door Size', form.doorSize + ' inches'],
            ['Kick Plate', form.kickPlate === 'none' ? 'None — Sin Kick Plate' : form.kickPlate ? `${form.kickPlate} inches` : ''],
          ].map(([label, value]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: '14px' }}>
              <span style={{ color: '#666' }}>{label}</span>
              <span style={{ fontWeight: '500' }}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="action-buttons">
        <button className="btn-secondary" onClick={() => navigate('/form', { state })}>Back</button>
        <button className="btn-primary" onClick={handleCalculate}>Calculate Materials →</button>
      </div>
    </div>
  );
}

export default Review;